import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlarmClock,
  AppWindow,
  Bell,
  Cast,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Clapperboard,
  Globe,
  GripVertical,
  Home,
  Info,
  LayoutGrid,
  List,
  Map as MapIcon,
  MonitorPlay,
  MoreVertical,
  Music,
  RotateCcw,
  Save,
  Settings,
  Sparkles,
  Star,
  SunMedium,
  Tv,
  UtensilsCrossed,
  type LucideIcon,
} from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { api, type MenuItem, type Site } from '../api/client';
import { NeedsSite } from '../app/Layout';
import { useSession, useSite } from '../app/session';
import { Menu } from '../components/Menu';
import { Select } from '../components/Select';
import { ErrorBox, Loading, PageHeader, Toggle } from '../components/ui';

/** Keys of the icon set shipped in the TV app (admin picks from these; no arbitrary uploads on TV). */
export const ICONS: Record<string, LucideIcon> = {
  tv: Tv,
  list: List,
  film: Clapperboard,
  music: Music,
  bell: Bell,
  dining: UtensilsCrossed,
  info: Info,
  apps: LayoutGrid,
  alarm: AlarmClock,
  star: Star,
  spa: Sparkles,
  map: MapIcon,
  weather: SunMedium,
  globe: Globe,
  cast: Cast,
  settings: Settings,
};
const TINT: Record<string, [string, string]> = {
  tv: ['#6d28d9', '#f1ebff'],
  list: ['#2563eb', '#e8f0ff'],
  film: ['#e11d48', '#ffe9ee'],
  music: ['#2563eb', '#e8f0ff'],
  bell: ['#059669', '#e6f7f0'],
  dining: ['#ea580c', '#fff1e6'],
  info: ['#2563eb', '#e8f0ff'],
  apps: ['#059669', '#e6f7f0'],
  alarm: ['#e11d48', '#ffe9ee'],
};
const PLACEMENT: Record<MenuItem['placement'], string> = { BOTH: 'Home + sidebar', HOME: 'Home only', SIDEBAR: 'Sidebar only', APPS: 'Apps page tile' };
const iconOf = (m: MenuItem) => m.icon || m.defaultIcon;
export const Glyph = ({ name, size = 18 }: { name: string; size?: number }) => {
  const I = ICONS[name] ?? AppWindow;
  return <I size={size} />;
};

export function TvMenuPage() {
  const site = useSite();
  return <NeedsSite>{site ? <MenuEditor key={site.id} site={site} /> : <PageHeader title="TV Menu" />}</NeedsSite>;
}

function StatCard({ icon, tone, value, label }: { icon: ReactNode; tone: string; value: number; label: string }) {
  return (
    <div className="stat-tile">
      <span className={`stat-tile-icon ${tone}`}>{icon}</span>
      <span>
        <span className="stat-tile-label">{label}</span>
        <span className="stat-tile-value">{value}</span>
      </span>
    </div>
  );
}

function MenuEditor({ site }: { site: Site }) {
  const { can } = useSession();
  const qc = useQueryClient();
  const menu = useQuery({ queryKey: ['menu', site.id], queryFn: () => api.get<{ items: MenuItem[] }>(`/sites/${site.id}/menu`) });
  const [draft, setDraft] = useState<MenuItem[] | null>(null);
  const [drag, setDrag] = useState<number | null>(null);
  const [saved, setSaved] = useState(false);
  const items = draft ?? menu.data?.items ?? [];
  const save = useMutation({
    mutationFn: () =>
      api.put(`/sites/${site.id}/menu`, {
        items: items.map((m, i) => ({ key: m.key, enabled: m.enabled, sortOrder: i + 1, label: m.label || null, icon: m.icon || null, placement: m.placement })),
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['menu', site.id] });
      setDraft(null);
      setSaved(true);
    },
  });
  useEffect(() => {
    if (draft) setSaved(false);
  }, [draft]);
  const update = (i: number, patch: Partial<MenuItem>) => setDraft(items.map((m, j) => (j === i ? { ...m, ...patch } : m)));
  const moveTo = (from: number, to: number) => {
    if (to < 0 || to >= items.length || from === to) return;
    const next = [...items];
    const [m] = next.splice(from, 1);
    next.splice(to, 0, m!);
    setDraft(next);
  };
  if (menu.isLoading) return <Loading />;
  const editable = can('ADMIN');
  const on = items.filter((m) => m.enabled && m.available);

  return (
    <>
      <PageHeader
        title="TV Menu"
        sub="Enable, reorder, rename and choose icons for the modules shown on the TV. Saved changes reach the TVs within seconds, without a restart."
        actions={
          editable && (
            <>
              {save.isSuccess && saved && <span className="badge ok">Published</span>}
              <button className="btn" disabled={!draft} onClick={() => setDraft(null)} title="Undo the changes you haven't saved">
                <RotateCcw size={16} /> Reset
              </button>
              <button className="btn primary" disabled={!draft || save.isPending} onClick={() => save.mutate()}>
                <Save size={16} /> Save &amp; publish
              </button>
            </>
          )
        }
      />
      <div className="stat-strip">
        <StatCard icon={<LayoutGrid size={22} />} tone="violet" value={items.length} label="Total modules" />
        <StatCard icon={<CheckCircle2 size={22} />} tone="green" value={on.length} label="Enabled" />
        <StatCard icon={<Home size={22} />} tone="violet" value={on.filter((m) => m.placement !== 'APPS').length} label="Home + sidebar" />
        <StatCard icon={<AppWindow size={22} />} tone="violet" value={on.filter((m) => m.placement === 'APPS').length} label="Apps page tiles" />
      </div>

      <div className="menu-layout">
        <div className="card">
          <div className="pkg-head">
            <span className="pkg-head-icon">
              <LayoutGrid size={22} />
            </span>
            <span>
              <span className="pkg-head-title">Menu modules</span>
              <span className="muted" style={{ display: 'block', fontSize: 13 }}>
                Drag to reorder. Changes reach the TV menu after you save.
              </span>
            </span>
          </div>
          <div className="table-wrap" style={{ marginTop: 14 }}>
            <table className="menu-table">
              <thead>
                <tr>
                  <th style={{ width: 96 }}>Order</th>
                  <th>Module</th>
                  <th>Label on TV</th>
                  <th>Icon</th>
                  <th>Shown in</th>
                  <th>Status</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((m, i) => {
                  const [fg, bg] = TINT[iconOf(m)] ?? ['#6d28d9', '#f1ebff'];
                  return (
                    <tr
                      key={m.key}
                      className={drag === i ? 'dragging' : undefined}
                      style={!m.available ? { opacity: 0.55 } : undefined}
                      draggable={editable}
                      onDragStart={(e) => {
                        setDrag(i);
                        e.dataTransfer.effectAllowed = 'move';
                      }}
                      onDragOver={(e) => {
                        if (drag === null || drag === i) return;
                        e.preventDefault();
                        moveTo(drag, i);
                        setDrag(i);
                      }}
                      onDragEnd={() => setDrag(null)}
                    >
                      <td>
                        <span className="row" style={{ gap: 2, flexWrap: 'nowrap' }}>
                          <span className="drag-handle" title="Drag to reorder">
                            <GripVertical size={16} />
                          </span>
                          <button className="icon-btn" disabled={!editable || i === 0} onClick={() => moveTo(i, i - 1)} title="Move up">
                            <ChevronUp size={16} />
                          </button>
                          <button className="icon-btn" disabled={!editable || i === items.length - 1} onClick={() => moveTo(i, i + 1)} title="Move down">
                            <ChevronDown size={16} />
                          </button>
                        </span>
                      </td>
                      <td>
                        <span className="row" style={{ gap: 10, flexWrap: 'nowrap' }}>
                          <span className="module-icon" style={{ color: fg, background: bg }}>
                            <Glyph name={iconOf(m)} />
                          </span>
                          <span>
                            <strong style={{ whiteSpace: 'nowrap' }}>{m.defaultLabel}</strong>
                            {!m.available && <span className="muted" style={{ display: 'block', fontSize: 12 }}>Coming later</span>}
                          </span>
                        </span>
                      </td>
                      <td>
                        <input value={m.label ?? ''} placeholder={m.defaultLabel} disabled={!editable} onChange={(e) => update(i, { label: e.target.value })} />
                      </td>
                      <td>
                        <span className="icon-select">
                          <Glyph name={iconOf(m)} size={16} />
                          <Select value={iconOf(m)} disabled={!editable} onChange={(e) => update(i, { icon: e.target.value })} aria-label="Icon">
                            {Object.keys(ICONS).map((ic) => (
                              <option key={ic}>{ic}</option>
                            ))}
                          </Select>
                        </span>
                      </td>
                      <td>
                        <Select value={m.placement} disabled={!editable} onChange={(e) => update(i, { placement: e.target.value as MenuItem['placement'] })} aria-label="Shown in">
                          {Object.entries(PLACEMENT).map(([k, v]) => (
                            <option key={k} value={k}>
                              {v}
                            </option>
                          ))}
                        </Select>
                      </td>
                      <td>
                        <Toggle on={m.enabled} onChange={(v) => editable && m.available && update(i, { enabled: v })} label="Enabled" />
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {editable && (
                          <Menu
                            minWidth={200}
                            button={({ onClick }) => (
                              <button type="button" className="icon-btn" title="More" onClick={onClick}>
                                <MoreVertical size={16} />
                              </button>
                            )}
                          >
                            <button type="button" className="more-item" onClick={() => moveTo(i, 0)}>
                              <ChevronUp size={15} /> Move to top
                            </button>
                            <button type="button" className="more-item" onClick={() => moveTo(i, items.length - 1)}>
                              <ChevronDown size={15} /> Move to bottom
                            </button>
                            <button type="button" className="more-item" onClick={() => update(i, { label: null, icon: null })}>
                              <RotateCcw size={15} /> Default label and icon
                            </button>
                          </Menu>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <ErrorBox error={save.error} />
        </div>

        <TvPreview site={site} items={items} />
      </div>
    </>
  );
}

function TvPreview({ site, items }: { site: Site; items: MenuItem[] }) {
  const b = site.branding as Record<string, unknown>;
  const assets = useQuery({ queryKey: ['assets'], queryFn: () => api.get<{ items: { id: string; url: string }[] }>('/assets') });
  const urlOf = (id: unknown) => (id ? assets.data?.items.find((a) => a.id === id)?.url : undefined);
  const bg = urlOf(b.homeBackgroundAssetId);
  const logo = urlOf(b.hotelLogoAssetId);
  const accent = (b.accentColor as string) || '#6E4BFF';
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(t);
  }, []);
  const opts = { timeZone: site.timezone } as const;
  const shown = items.filter((m) => m.available && m.placement !== 'APPS');
  return (
    <div className="card menu-preview-card">
      <div className="pkg-head">
        <span className="pkg-head-icon">
          <MonitorPlay size={22} />
        </span>
        <span>
          <span className="pkg-head-title">TV Menu Preview</span>
          <span className="muted" style={{ display: 'block', fontSize: 13 }}>
            How the menu appears on the TV.
          </span>
        </span>
      </div>
      <div className="tv-menu-preview" style={bg ? { backgroundImage: `linear-gradient(90deg, rgba(10,8,30,.82) 30%, rgba(10,8,30,.15)), url(${bg})` } : undefined}>
        <div className="tvp-head">
          {logo ? <img src={logo} alt="" /> : <strong>{site.name}</strong>}
          <span style={{ textAlign: 'right' }}>
            <span className="tvp-time">{now.toLocaleTimeString([], { ...opts, hour: '2-digit', minute: '2-digit', hour12: !!b.hour12 })}</span>
            <span className="tvp-date">{now.toLocaleDateString([], { ...opts, weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}</span>
          </span>
        </div>
        <div className="tvp-list">
          {shown.map((m, i) => (
            <div key={m.key} className={`tvp-item${m.enabled ? '' : ' off'}`} style={i === 0 && m.enabled ? { background: accent, borderColor: 'rgba(255,255,255,.5)' } : undefined}>
              <Glyph name={iconOf(m)} size={17} />
              {m.label || m.defaultLabel}
            </div>
          ))}
        </div>
        {items.some((m) => m.available && m.enabled && m.placement === 'APPS') && (
          <div className="tvp-note">
            + {items.filter((m) => m.available && m.enabled && m.placement === 'APPS').map((m) => m.label || m.defaultLabel).join(', ')} on the Apps page
          </div>
        )}
      </div>
    </div>
  );
}
